/**
 * Page Header Component
 */
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Add as AddIcon, NavigateNext } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

interface Breadcrumb {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  actionLabel?: string;
  actionIcon?: React.ReactNode;
  onAction?: () => void;
  actionPath?: string;
  action?: React.ReactNode;
}

const PageHeader = ({
  title,
  subtitle,
  breadcrumbs,
  actionLabel,
  actionIcon = <AddIcon />,
  onAction,
  actionPath,
  action,
}: PageHeaderProps) => {
  const navigate = useNavigate();

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else if (actionPath) {
      navigate(actionPath);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 1 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return isLast ? (
              <Typography
                key={crumb.label}
                color="text.primary"
                variant="body2"
              >
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={crumb.label}
                component={RouterLink}
                to={crumb.path || '/'}
                color="inherit"
                underline="hover"
                variant="body2"
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action ? (
          action
        ) : actionLabel ? (
          <Button
            variant="contained"
            startIcon={actionIcon}
            onClick={handleAction}
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' }, whiteSpace: { xs: 'normal', sm: 'nowrap' } }}
          >
            {actionLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default PageHeader;

